/**
 * MCP bridge wire protocol (Phase: AI agent control surface).
 *
 * The app-side bridge client and the Node-side MCP relay
 * (tools/mcp-server/index.mjs) speak this exact contract over local HTTP.
 * The AI agent talks MCP (JSON-RPC over stdio) to the relay; the relay
 * forwards tool invocations to the running app through this protocol:
 *
 *   GET  /health                -> { ok, appConnected, toolCount }
 *   POST /bridge/hello          -> app announces itself + tool catalog
 *   POST /bridge/poll           -> relay hands the app the next queued request
 *   POST /bridge/result         -> app returns the tool execution outcome
 *
 * Keep both sides in sync when extending the shapes below.
 */

/** One queued tool invocation from the relay to the app. */
export interface BridgeRequest {
    /** Relay-assigned unique id; echoed back with the result. */
    id: string;
    /** MCP tool name, e.g. 'yap_execute_command'. */
    tool: string;
    /** Tool arguments object (already JSON-parsed). */
    args: Record<string, unknown>;
    /** Epoch ms when the relay queued the request (for timeouts). */
    queuedAt: number;
}

/** Tool execution outcome produced by the in-app router. */
export interface BridgeToolResult {
    ok: boolean;
    /** Human/agent-readable text summary (becomes MCP text content). */
    text?: string;
    /** Structured payload (becomes MCP JSON text content when present). */
    data?: unknown;
    /** PNG base64 (no data-url prefix) for screenshot tools (MCP image content). */
    imageBase64?: string;
    /** Error explanation when ok === false. */
    error?: string;
}

/** Result envelope the app POSTs back for one BridgeRequest. */
export interface BridgeResultEnvelope {
    id: string;
    clientId: string;
    result: BridgeToolResult;
}

/** Tool descriptor shipped to the relay so tools/list mirrors the app. */
export interface BridgeToolSpec {
    name: string;
    description: string;
    /** JSON Schema for inputSchema (MCP tools/list contract). */
    inputSchema: Record<string, unknown>;
}

/** App identity sent once per connection, resent on reconnect. */
export interface BridgeHelloPayload {
    clientId: string;
    appName: string;
    appVersion: string;
    /** Workspace route/mode the app reports at hello time. */
    mode: string;
    tools: BridgeToolSpec[];
}

/** Response for /bridge/poll: either one request or an idle tick. */
export type BridgePollResponse = { idle: true } | ({ idle: false } & BridgeRequest);

/** Response for /health (relay side) — mirrored for the settings UI. */
export interface BridgeHealth {
    ok: boolean;
    appConnected: boolean;
    toolCount: number;
}

/** localStorage-persisted bridge settings key. */
export const MCP_BRIDGE_SETTINGS_KEY = 'yap-mcp-bridge-settings';

/** Default local relay port; must match tools/mcp-server default and the suite
 *  register's yap bridge entry (8912 = 8900+12). 8721 is retired — it belongs to
 *  Pik's app link now, so the old default made the app hello the wrong listener. */
export const MCP_DEFAULT_PORT = 8912;

export function bridgeBaseUrl(port: number): string {
    return `http://127.0.0.1:${port}`;
}
